import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.address) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      const sessionId = localStorage.getItem('sessionId');
      if (!sessionId) {
        toast.error('Your cart is empty');
        navigate('/cart');
        return;
      }

      await axios.post(`${API}/orders`, {
        session_id: sessionId,
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        shipping_address: formData.address
      });

      toast.success('Order placed successfully!');
      localStorage.removeItem('sessionId');
      navigate('/order-success');
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-24 px-6 md:px-12 lg:px-24" data-testid="checkout-page">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-5xl font-heading font-bold text-[#1F2937] mb-12" data-testid="checkout-heading">
          Checkout
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="checkout-form">
          <div className="bg-white border border-[#E5E4DE] p-8 space-y-6">
            <h2 className="text-2xl font-heading font-semibold text-[#1F2937] mb-4" data-testid="shipping-heading">
              Shipping Information
            </h2>

            <div className="space-y-2">
              <Label htmlFor="name" data-testid="name-label">Full Name</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe"
                required
                data-testid="name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" data-testid="email-label">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="john@example.com"
                required
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" data-testid="phone-label">Phone Number</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(555) 123-4567"
                required
                data-testid="phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address" data-testid="address-label">Shipping Address</Label>
              <Textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="123 Main St, City, State, ZIP"
                rows={4}
                required
                data-testid="address-input"
              />
            </div>
          </div>

          <div className="bg-[#F5F5F0] p-8">
            <h2 className="text-2xl font-heading font-semibold text-[#1F2937] mb-4" data-testid="payment-heading">
              Payment Information
            </h2>
            <p className="text-base text-[#5D5C56] mb-4" data-testid="payment-notice">
              Payment gateway integration coming soon. Your order will be confirmed, and we'll contact you for payment details.
            </p>
            <div className="flex items-center gap-3 text-sm text-[#5D5C56]" data-testid="payment-methods">
              <span className="font-medium">We accept:</span>
              <span>Mastercard</span>
              <span>•</span>
              <span>Visa</span>
              <span>•</span>
              <span>Truist</span>
              <span>•</span>
              <span>Bank of America</span>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full"
            data-testid="place-order-button"
          >
            {submitting ? 'Placing Order...' : 'Place Order'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Checkout;